<script lang="ts">
  import { useThrelte } from '@threlte/core'
  import Camera from '../camera/Camera.svelte'
  import { interactivity, TransformControls } from '@threlte/extras'
  import { setStoreScene, setStoreSceneInvalidate } from '../../stores/scene'
  import { cameraControls } from '../../stores/camera'
  import { debounce } from 'es-toolkit'
  import { getColorFromTheme } from '../../helpers/theme'
  import { Color } from 'three'
  import { ThreeItemKeys } from '../../constants/three'
  import useCanvasSelection from '../../hooks/canvas-selection'
  import useObjectQueries from '../../routes/project/hooks/useObjectQueries'
  import { ROTATION_DRAG_SNAP, snapRotationToNearestStep } from '../../helpers/rotation-snap'
  import { sceneMode } from '../../stores/scene-mode'
  import EscapeListener from './EscapeListener.svelte'
  import SelectionListener from './SelectionListener.svelte'
  import AssignmentListener from './AssignmentListener.svelte'
  import ForbiddenZoneListener from './ForbiddenZoneListener.svelte'
  import ToggleCameraListener from './ToggleCameraListener.svelte'
  import TransformControlsListener from './TransformControlsListener.svelte'
  import SeamListener from './SeamListener.svelte'
  import FocusListener from './FocusListener.svelte'
  import PlacementListener from './PlacementListener.svelte'
  import CursorListener from './CursorListener.svelte'
  import SceneDoubleClickFocusListener from './SceneDoubleClickFocusListener.svelte'
  import GroupTransformControls from './GroupTransformControls.svelte'

  const { scene, invalidate, dom } = useThrelte()
  const { object: canvasSelection, multiSelection } = useCanvasSelection()
  const { useObject, invalidateCanvas } = useObjectQueries()
  const objectQuery = $derived(useObject($canvasSelection?.uuid))
  const object = $derived(objectQuery.data)
  const selectedUuids = $derived(
    [
      $canvasSelection?.uuid,
      ...$multiSelection.map(item => item.uuid),
    ].filter((uuid): uuid is string => !!uuid),
  )

  $effect(() => {
    window.__THRELTE_SCENE__ = scene
    setStoreScene(scene)
    setStoreSceneInvalidate(invalidate)
  })

  $effect(() => {
    scene.background = new Color(
      getColorFromTheme('--printer-pane-active-background'),
    )
  })

  interactivity()

  const debouncedInvalidateCanvas = debounce(() => {
    invalidateCanvas()
  }, 500)

  function onTransformControlChange() {
    if ($sceneMode === 'rotate' && object) {
      snapRotationToNearestStep(object)
    }
    debouncedInvalidateCanvas()
  }
</script>

<AssignmentListener />
<CursorListener />
<EscapeListener />
<FocusListener />
<SceneDoubleClickFocusListener />
<ForbiddenZoneListener />
<SelectionListener />
<SeamListener />
<PlacementListener />
<ToggleCameraListener />
<TransformControlsListener />
<Camera />

{#if selectedUuids.length > 1}
  {#key selectedUuids.join()}
    {#if $sceneMode === 'translate' || $sceneMode === 'rotate'}
      <GroupTransformControls objectUuids={selectedUuids} />
    {/if}
  {/key}
{:else}
  {#key [$sceneMode, object?.uuid, objectQuery.dataUpdatedAt].join()}
    {#if object && ($sceneMode === 'translate' || $sceneMode === 'rotate')}
      <TransformControls
        autoPauseOrbitControls
        autoPauseTrackballControls
        mode={$sceneMode === 'rotate' ? 'rotate' : 'translate'}
        rotationSnap={ROTATION_DRAG_SNAP}
        {object}
        onmouseDown={() => {
          $cameraControls?.disconnect()
        }}
        onmouseUp={() => {
          $cameraControls?.connect(dom)
        }}
        onobjectChange={onTransformControlChange}
        showX={$sceneMode === 'rotate'
          ? !object.userData[ThreeItemKeys.ROTATION_LOCKED_X]
          : !object.userData[ThreeItemKeys.TRANSLATION_LOCKED_X]}
        showY={$sceneMode === 'rotate'
          ? !object.userData[ThreeItemKeys.ROTATION_LOCKED_Y]
          : !object.userData[ThreeItemKeys.TRANSLATION_LOCKED_Y]}
        showZ={$sceneMode === 'rotate'
          ? !object.userData[ThreeItemKeys.ROTATION_LOCKED_Z]
          : !object.userData[ThreeItemKeys.TRANSLATION_LOCKED_Z]}
      />
    {/if}
  {/key}
{/if}
